import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { Tooltip, IconButton, Box, Typography, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGridPro } from '@mui/x-data-grid-pro';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useSelector, useDispatch } from '../../redux/store';
import { openErrorDialog } from '../../redux/slices/orders';
import { REPLACE_VENDOR_ITEM, VENDOR_ITEM } from './pendingOrderColumns';
import ReplaceItemQuantityField from './ReplaceItemQuantityField';
import { useScopeCheck } from '../scopes';
import CustomDataGrid from './CustomDataGrid';
import axiosInstance from '../../utils/axios';
import CloseIconButton from '../basic-buttons/CloseIconButton';
import LoadingAnimation from '../loader/LoadingAnimation';

VendorField.propTypes = {
  row: PropTypes.object,
};

export default function VendorField({ row }) {
  const dispatch = useDispatch();

  const hasAccess = useScopeCheck(['Edit-Order'], true);
  const { id = '' } = useParams();
  const { cartCheckItemsById } = useSelector((state) => state.orders || {});
  const errorArray = useMemo(() => {
    const cartCheckItems = cartCheckItemsById?.[id];
    if (!cartCheckItems?.length) return [];
    const cartCheckItem = cartCheckItems?.find((cci) => cci.id === row.id) || {};
    return cartCheckItem && cartCheckItem.errors ? cartCheckItem.errors : [];
  }, [cartCheckItemsById, id, row]);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const handleClickOpen = (productId) => {
    setLoading(true);
    setOpen(true);
    axiosInstance.get(`/ConnectedItems?productId=${productId}&orderId=${id}`)
      .then(res => setRows(res.data.filter((item) => item.id !== row.itemId)))
      .catch(() => enqueueSnackbar("Failed to load items", { variant: "error" }))
      .finally(() => setLoading(false));
  };

  const handleClose = () => setOpen(false);
  const openDialog = () =>  dispatch(openErrorDialog());

  const qtyTotalAction = {
    field: 'qtyTotalAction',
    headerName: 'Qty',
    width: 450,
    renderCell: (params) => <ReplaceItemQuantityField errorRow={row} row={params.row} handleClose={handleClose} />,
    id: 'qty',
    className: 'qty',
  };

  return (
    <>
      <Box sx={{ display: 'flex', '&:hover': { cursor: errorArray.length > 0 ? 'pointer' : 'default' } }}>
        <Tooltip title={errorArray.length > 0 ? errorArray.map((error) => (<div key={error}>{error}<br /></div>)) : ''}>
          <Typography
            variant="subtitle2"
            color="secondary"
            component="span"
            onClick={errorArray?.length > 0 ? openDialog : null}
            sx={{
              textDecoration: errorArray.length > 0 ? 'underline' : null,
              color: errorArray.length > 0 ? 'error.main' : null,
              mt: 0.5,
              mr: 0.2,
            }}
          >
            {row.vendor || 'N/A'}
          </Typography>
        </Tooltip>
        {row.hasConnectedItems && hasAccess && (
          <Tooltip title="Change vendor" disableHoverListener={row?.deleted === true}>
            <Typography component="span">
              <IconButton
                color="secondary"
                onClick={() => handleClickOpen(row.productId)}
                sx={{ padding: '0px' }}
                disabled={row?.deleted === true}
              >
                <SwapHorizIcon />
              </IconButton>
            </Typography>
          </Tooltip>
        )}
      </Box>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        maxWidth="full"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          Change Vendor
          <CloseIconButton onClick={handleClose} />
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Current item
          </Typography>
          <div style={{ width: '100%' }}>
            <StyledDataGrid
              getRowHeight={() => 'auto'}
              rows={[row]}
              columns={REPLACE_VENDOR_ITEM}
              getEstimatedRowHeight={() => 44}
              disableRowSelectionOnClick
            />
          </div>
          <Typography variant="subtitle1" sx={{ mt: 2, textAlign: 'center' }}>
            Change to
          </Typography>
          {loading ? <LoadingAnimation /> : <div style={{ width: '100%' }}>
            <CustomDataGrid
              gridId="vendorFieldDataGrid"
              getRowHeight={() => 'auto'}
              data={rows}
              isLoading={false}
              gridColumns={[...VENDOR_ITEM, qtyTotalAction]}
              disableRowSelectionOnClick
              sx={{ '& .MuiDataGrid-cell': { cursor: 'default !important' } }}
              isModal
            />
          </div>}
        </DialogContent>
      </Dialog>
    </>
  );
}

const StyledDataGrid = styled(DataGridPro)(() => ({
  '& .MuiDataGrid-main': {
    backgroundColor: '#fff',
  },
  '& .MuiDataGrid-row': {
    border: 'none',
    backgroundColor: '#fff',
  },
  '& .MuiDataGrid-row:hover': {
    cursor: 'default',
    backgroundColor: '#fff',
  },
}));
